import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "@zorni/components-lib";

import styles from "./BookingRecap.module.css";
import { currentLocale } from "../../../../common/i18n/locale";

export const formatAmount = (amount) => {
  const value = Number(amount?.value);
  if (!Number.isFinite(value)) return null;
  return `${value.toLocaleString(currentLocale())}${amount?.currency ? ` ${amount.currency}` : ""}`;
};

const formatDay = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(currentLocale(), { day: "2-digit", month: "short" });
};

/**
 * Récapitulatif de la réservation : photo, dates, montants et action
 * principale. Sur téléphone, la carte cède la place à une barre fixe en bas
 * qui garde le total et le bouton à portée de pouce.
 */
const BookingRecap = ({
  picture,
  title,
  kicker,
  startDate,
  endDate,
  startPlace,
  durationLabel,
  lines = [],
  total,
  totalNote,
  ctaLabel,
  onContinue,
  ctaDisabled = false,
  secondary,
}) => {
  const { t } = useTranslation();
  const totalLabel = formatAmount(total);

  const body = (
    <div className={styles.body}>
      <div className={styles.dates}>
        <div>
          <small>{t("booking.recap.departure", "Départ")}</small>
          <strong>{formatDay(startDate)}</strong>
          {startPlace && <span>{startPlace}</span>}
        </div>
        <i aria-hidden="true">
          <Icon name="iconArrowRight" size="md" />
        </i>
        <div>
          <small>{t("booking.recap.return", "Retour")}</small>
          <strong>{formatDay(endDate)}</strong>
          {durationLabel && <span>{durationLabel}</span>}
        </div>
      </div>

      {lines.length > 0 && (
        <div className={styles.lines}>
          {lines.map((line) => (
            <p key={line.label} className={styles.line}>
              <span>{line.label}</span>
              <b>{formatAmount(line.amount) ?? "—"}</b>
            </p>
          ))}
        </div>
      )}

      <p className={styles.total}>
        <span>{t("booking.recap.total", "Total")}</span>
        <b>{totalLabel ?? "—"}</b>
      </p>

      {totalNote && <p className={styles.note}>{totalNote}</p>}

      <Button
        variant="primary"
        size="large"
        fullWidth
        iconName="iconArrowRight"
        iconPosition="right"
        label={ctaLabel}
        onClick={onContinue}
        disabled={ctaDisabled}
      />

      <div className={styles.reassure}>
        <p>
          <Icon name="iconLock" size="sm" />
          {t("booking.recap.noCharge", "Aucun montant débité à cette étape.")}
        </p>
        <p>
          <Icon name="iconShieldCheck" size="sm" />
          {t("booking.recap.support", "Assistance avant et pendant le voyage.")}
        </p>
      </div>
    </div>
  );

  return (
    <>
      <aside className={styles.aside} aria-label={t("booking.recap.aria", "Récapitulatif de la réservation")}>
        <div className={styles.card}>
          <div className={styles.media}>
            {picture ? <img src={picture} alt="" /> : <span className={styles.mediaFallback} />}
            <div className={styles.mediaTitle}>
              {kicker && <small>{kicker}</small>}
              <strong>{title}</strong>
            </div>
          </div>
          {body}
        </div>
        {secondary}
      </aside>

      <div className={styles.bottomBar}>
        <p className={styles.bottomTotal}>
          <small>{t("booking.recap.total", "Total")}</small>
          <strong>{totalLabel ?? "—"}</strong>
        </p>
        <Button
          variant="primary"
          size="medium"
          label={ctaLabel}
          onClick={onContinue}
          disabled={ctaDisabled}
        />
      </div>
    </>
  );
};

BookingRecap.propTypes = {
  picture: PropTypes.string,
  title: PropTypes.string,
  kicker: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  startPlace: PropTypes.string,
  durationLabel: PropTypes.string,
  lines: PropTypes.array,
  total: PropTypes.object,
  totalNote: PropTypes.string,
  ctaLabel: PropTypes.string,
  onContinue: PropTypes.func,
  ctaDisabled: PropTypes.bool,
  secondary: PropTypes.node,
};

export default BookingRecap;
